import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Users, DollarSign, UserPlus, AlertCircle, Activity, TrendingUp, Clock, CalendarDays, Dumbbell, UtensilsCrossed, Heart, Trophy, ChevronRight, Bell } from 'lucide-react';
import { TrendArrow } from '@/components/WeeklyDigest';
import { NutritionExceptions } from '@/components/NutritionExceptions';
import { PIPELINE } from './Clients';

function PipelineCounters({ counts, nav }) {
  if (!counts) return null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="client-pipeline">
      {PIPELINE.map(p => (
        <button key={p.key} onClick={() => nav(`/admin/clients?filter=stage_${p.key}`)} data-testid={`pipeline-${p.key}`}
          className="regen-card-elevated p-4 text-left hover:-translate-y-0.5 transition-transform" style={{ borderTop: `3px solid ${p.color}` }}>
          <div className="font-mono text-3xl font-bold" style={{ color: p.color }} data-testid={`pipeline-count-${p.key}`}>{counts[p.key] ?? 0}</div>
          <div className="text-xs text-muted-foreground mt-1">{p.label}</div>
        </button>
      ))}
    </div>
  );
}

const pctColor = (v) => v == null ? '#7E8C9E' : v >= 85 ? '#65A30D' : v >= 60 ? '#FFB800' : '#FF5C5C';

export default function AdminDashboard() {
  const [a, setA] = useState(null);
  const [digests, setDigests] = useState(null);
  const [flags, setFlags] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get('/analytics').then(r => setA(r.data));
    api.get('/weekly-digests').then(r => setDigests(r.data)).catch(() => setDigests([]));
    api.get('/admin/client-flags').then(r => setFlags(r.data)).catch(() => setFlags([]));
    api.get('/admin/notifications').then(r => setNotifs(r.data)).catch(() => setNotifs({ items: [], unread: 0 }));
    api.get('/admin/client-pipeline').then(r => setPipeline(r.data)).catch(() => {});
  }, []);

  const dismissNotifs = async () => {
    await api.post('/admin/notifications/read-all').catch(() => {});
    setNotifs(n => ({ ...n, unread: 0 }));
  };

  if (!a) return <div className="text-muted-foreground">Loading…</div>;

  const cards = [
    { label: 'Active Clients', value: a.active_clients, icon: Users, color: '#00C8FF', sub: `${a.total_clients} total`, to: '/admin/clients?filter=active' },
    { label: 'Monthly Revenue', value: `$${a.monthly_revenue.toLocaleString()}`, icon: DollarSign, color: '#65A30D', sub: 'Last 30 days', to: '/admin/payments' },
    { label: 'New Clients', value: a.new_clients_30d, icon: UserPlus, color: '#FFB800', sub: 'Last 30 days', to: '/admin/clients?filter=new' },
    { label: 'Outstanding', value: a.outstanding_payments, icon: AlertCircle, color: '#FF8A4C', sub: 'Pending', to: '/admin/payments' },
    { label: 'Avg YourReGen Score', value: a.avg_regen_score, icon: Activity, color: '#7DD3FC', sub: 'All clients', to: '/admin/clients' },
    { label: 'At Risk', value: a.clients_at_risk, icon: Clock, color: '#FF8A4C', sub: 'Score < 40', to: '/admin/clients?filter=at_risk' },
  ];

  const actionCards = [
    { label: 'New Plus', value: a.new_plus ?? 0, color: '#00C8FF', sub: 'Automated — no prep', to: '/admin/clients?filter=new_plus' },
    { label: 'Coaching — Action Needed', value: a.new_coaching_action ?? 0, color: '#FF8A4C', sub: 'Prepare their programme', to: '/admin/clients?filter=new_coaching', urgent: true },
    { label: 'New Tracker', value: a.new_tracker ?? 0, color: '#7DD3FC', sub: 'Self-serve', to: '/admin/clients?filter=new_tracker' },
  ];

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <div>
        <div className="label-mini text-[#00C8FF]">Overview</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl mt-1">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">Track your coaching business performance.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
        {cards.map(c => (
          <div key={c.label} onClick={() => nav(c.to)} role="button" tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); nav(c.to); } }}
            className="regen-card-elevated p-4 lg:p-5 cursor-pointer hover:translate-y-[-2px] hover:shadow-lg transition focus:outline-none focus:ring-2 focus:ring-[#00C8FF]"
            data-testid={`kpi-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${c.color}1a`, color: c.color }}>
                <c.icon className="w-5 h-5" />
              </div>
              <div className="label-mini">{c.label}</div>
            </div>
            <div className="font-mono text-3xl mt-3">{c.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Actionable new-client counts by programme type */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
        {actionCards.map(c => (
          <div key={c.label} onClick={() => nav(c.to)} role="button" tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); nav(c.to); } }}
            className={`regen-card-elevated p-4 cursor-pointer hover:translate-y-[-2px] transition focus:outline-none focus:ring-2 focus:ring-[#00C8FF] ${c.urgent && c.value > 0 ? 'border border-[#FF8A4C]/50 bg-[#FF8A4C]/5' : ''}`}
            data-testid={`action-${c.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <div className="flex items-center justify-between">
              <div className="label-mini">{c.label}</div>
              {c.urgent && c.value > 0 && <AlertCircle className="w-4 h-4 text-[#FF8A4C]" />}
            </div>
            <div className="font-mono text-2xl mt-2" style={{ color: c.value > 0 ? c.color : undefined }}>{c.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="regen-card-elevated p-5 lg:p-6 blueprint-grid">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-5 h-5 text-[#00C8FF]" />
          <div className="font-display font-bold text-lg lg:text-xl">Business Pulse</div>
        </div>
        <div className="text-sm text-muted-foreground leading-relaxed">
          {a.active_clients > 0 ? (
            <>You're coaching <button onClick={() => nav('/admin/clients?filter=active')} data-testid="pulse-active" className="text-[#00C8FF] font-semibold hover:underline">{a.active_clients} active clients</button> with an average YourReGen score of <button onClick={() => nav('/admin/clients')} data-testid="pulse-avg" className="text-[var(--c-green-ink)] font-semibold hover:underline">{a.avg_regen_score}</button>. {a.clients_at_risk > 0 && <>Heads up — <button onClick={() => nav('/admin/clients?filter=at_risk')} data-testid="pulse-at-risk" className="text-[#FF8A4C] font-semibold hover:underline">{a.clients_at_risk} client(s) at risk</button>. <button onClick={() => nav('/admin/messages')} data-testid="pulse-message" className="text-[#00C8FF] font-semibold hover:underline">Send them a message today.</button></>}</>
          ) : 'No active clients yet. Share your YourReGen onboarding link.'}
        </div>
      </div>

      {/* New client notifications */}
      <NutritionExceptions />

      <PipelineCounters counts={pipeline} nav={nav} />

      {notifs?.pending_reviews?.length > 0 && (
        <div className="regen-card-elevated p-4 border border-[#FFB800]/40" data-testid="pending-programme-reviews">
          <div className="flex items-center gap-3 mb-3">
            <Bell className="w-5 h-5 text-[#FFB800]" />
            <div className="font-display font-bold">{notifs.pending_reviews.length} programme{notifs.pending_reviews.length === 1 ? '' : 's'} awaiting your review</div>
          </div>
          <div className="space-y-2">
            {notifs.pending_reviews.map(n => (
              <button key={n.id} onClick={() => nav(`/admin/clients/${n.client_id}?tab=training`)} data-testid={`review-notif-${n.id}`}
                className="w-full flex items-center gap-3 rounded-xl bg-[#FFB800]/5 hover:bg-[#FFB800]/10 p-3 text-left transition">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${n.type === 'programme_generation_failed' ? 'bg-[#FF5C5C]' : 'bg-[#FFB800]'}`} />
                <div className="flex-1 min-w-0"><div className="text-sm font-medium">{n.title}</div><div className="text-[11px] text-muted-foreground">{n.body}</div></div>
                <span className="text-xs font-semibold text-[#FFB800] shrink-0">Review programme</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {notifs && notifs.unread > 0 && (
        <div className="regen-card-elevated p-4 border border-[#00C8FF]/30" data-testid="new-client-notifs">
          <div className="flex items-center gap-3 mb-3">
            <Bell className="w-5 h-5 text-[#00C8FF]" />
            <div className="font-display font-bold">{notifs.unread} new {notifs.unread === 1 ? 'notification' : 'notifications'}</div>
            <button onClick={dismissNotifs} data-testid="dismiss-notifs" className="ml-auto text-xs text-muted-foreground hover:text-foreground">Mark all read</button>
          </div>
          <div className="space-y-2">
            {notifs.items.filter(n => !n.read).slice(0, 5).map(n => (
              <button key={n.id} onClick={() => n.client_id && nav(`/admin/clients/${n.client_id}`)} data-testid={`notif-${n.id}`}
                className="w-full flex items-center gap-3 rounded-xl bg-[#00C8FF]/5 hover:bg-[#00C8FF]/10 p-3 text-left transition">
                <span className="w-2 h-2 rounded-full bg-[#00C8FF] flex-shrink-0" />
                <div className="flex-1 min-w-0"><div className="text-sm font-medium">{n.title}</div><div className="text-[11px] text-muted-foreground truncate">{n.body}</div></div>
                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Client Risk Triage */}
      <ClientRiskTriage flags={flags} nav={nav} />

      {/* Weekly Compliance Digest — all clients */}
      <div className="regen-card-elevated p-5 lg:p-6" data-testid="admin-weekly-digests">
        <div className="flex items-center gap-3 mb-4">
          <CalendarDays className="w-5 h-5 text-[var(--c-green-ink)]" />
          <div className="font-display font-bold text-lg lg:text-xl">Weekly Compliance Digest</div>
          <span className="text-xs text-muted-foreground ml-auto hidden sm:block">This week · per client</span>
        </div>
        {!digests ? <div className="text-sm text-muted-foreground">Loading…</div> : digests.length === 0 ? (
          <div className="text-sm text-muted-foreground">No clients yet.</div>
        ) : (
          <div className="space-y-2">
            <div className="hidden lg:grid grid-cols-[1.5fr_repeat(4,1fr)_1.5fr] gap-2 px-3 text-[10px] uppercase tracking-wider text-muted-foreground">
              <span>Client</span><span className="text-center">Training</span><span className="text-center">Nutrition</span><span className="text-center">Recovery</span><span className="text-center">Weight</span><span className="text-center">Bonus</span>
            </div>
            {digests.map(d => (
              <button key={d.client.id} onClick={() => nav(`/admin/clients/${d.client.id}`)} data-testid={`digest-row-${d.client.id}`}
                className="w-full grid grid-cols-2 lg:grid-cols-[1.5fr_repeat(4,1fr)_1.5fr] gap-2 items-center rounded-xl bg-[var(--c-film)] hover:bg-[var(--c-film)] p-3 text-left transition">
                <span className="text-sm font-medium truncate flex items-center gap-2">{d.client.name}<ChevronRight className="w-3 h-3 text-muted-foreground lg:hidden ml-auto" /></span>
                <Cell icon={Dumbbell} pct={d.training.scored_days > 0 ? d.training.compliance : null} delta={d.trends?.training?.delta} />
                <Cell icon={UtensilsCrossed} pct={d.nutrition.has_target ? d.nutrition.compliance : null} delta={d.trends?.nutrition?.delta} />
                <Cell icon={Heart} pct={d.recovery.score} delta={d.trends?.recovery?.delta} />
                <div className="text-center font-mono text-sm flex flex-col items-center" style={{ color: (d.weight.diff ?? 0) <= 0 ? '#65A30D' : '#FFB800' }}>
                  {d.weight.diff != null ? `${d.weight.diff > 0 ? '+' : ''}${d.weight.diff.toFixed(1)} kg` : '—'}
                </div>
                <div className="text-center font-mono text-sm text-[var(--c-green-ink)] flex items-center justify-center gap-1"><Trophy className="w-3.5 h-3.5" />+{d.bonus.minutes}m</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({ icon: Icon, pct, delta }) {
  return (
    <div className="text-center flex flex-col items-center">
      <div>
        <span className="lg:hidden inline-flex items-center justify-center mr-1"><Icon className="w-3 h-3 text-muted-foreground" /></span>
        <span className="font-mono text-sm font-bold" style={{ color: pctColor(pct) }}>{pct == null ? '—' : `${pct}%`}</span>
      </div>
      <TrendArrow delta={delta} />
    </div>
  );
}

const RISK = {
  at_risk: { label: 'At Risk', color: '#FF5C5C', dot: '🔴' },
  needs_attention: { label: 'Needs Attention', color: '#FFB800', dot: '🟡' },
  on_track: { label: 'On Track', color: '#65A30D', dot: '🟢' },
};

function ClientRiskTriage({ flags, nav }) {
  if (!flags) return null;
  const counts = { at_risk: 0, needs_attention: 0, on_track: 0 };
  flags.forEach(f => { counts[f.level] = (counts[f.level] || 0) + 1; });
  const needAttention = flags.filter(f => f.level !== 'on_track');

  return (
    <div className="regen-card-elevated p-5 lg:p-6" data-testid="client-risk-triage">
      <div className="flex items-center gap-3 mb-4">
        <AlertCircle className="w-5 h-5 text-[#FF5C5C]" />
        <div className="font-display font-bold text-lg lg:text-xl">Client Risk Triage</div>
        <span className="text-xs text-muted-foreground ml-auto hidden sm:block">Focus where it matters</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {['at_risk', 'needs_attention', 'on_track'].map(k => (
          <div key={k} className="rounded-xl bg-[var(--c-film)] p-3 text-center" data-testid={`risk-count-${k}`}>
            <div className="font-mono text-2xl" style={{ color: RISK[k].color }}>{counts[k]}</div>
            <div className="label-mini mt-1">{RISK[k].dot} {RISK[k].label}</div>
          </div>
        ))}
      </div>
      {needAttention.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-2">🎉 All clients are on track. Great coaching!</div>
      ) : (
        <div className="space-y-2">
          {needAttention.map(f => (
            <button key={f.id} onClick={() => nav(`/admin/clients/${f.id}`)} data-testid={`risk-row-${f.id}`}
              className="w-full flex items-center gap-3 rounded-xl bg-[var(--c-film)] hover:bg-[var(--c-film)] p-3 text-left transition">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: RISK[f.level].color }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{f.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{f.reasons.join(' · ') || RISK[f.level].label}</div>
              </div>
              <span className="font-mono text-sm" style={{ color: pctColor(f.regen) }}>{f.regen ?? '—'}</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
